import React, { useState, useCallback } from 'react';
import { DealUIObjectWithBrand } from 'common/api/deals/types';
import { ExternalLinkArrow } from '../ShopOnlineBanner';
import { colors } from 'styles/settings';
import { WalletAdLocation } from './WalletCardAd';
import { Btn } from 'components/btn/Btn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTicket } from '@fortawesome/pro-light-svg-icons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { Branding } from 'common/env';

export interface WalletCardAdViewProps {
    deal: DealUIObjectWithBrand;
    clickDetails: () => void;
    location: WalletAdLocation;
    onCtaClick: (url: string) => void;
}

export function WalletCardAdView({
    deal,
    clickDetails,
    location,
    onCtaClick,
}: WalletCardAdViewProps) {
    const [couponRevealed, setCouponRevealed] = useState(false);
    const [showCopiedIndicator, setShowCopiedIndicator] = useState(false);
    const isCoupon = deal.dealType === 'coupon' && deal.couponCode;
    const isCardDetails = location === 'gift_card_details';

    const handleCouponClick = useCallback(() => {
        if (deal.couponCode) {
            navigator.clipboard.writeText(deal.couponCode);
            setShowCopiedIndicator(true);
            setCouponRevealed(true);

            setTimeout(() => {
                setShowCopiedIndicator(false);
                onCtaClick(deal.url);
            }, 1500);
        }
    }, [deal.couponCode, deal.url, onCtaClick]);

    const handleDealClick = () => {
        onCtaClick(deal.url);
    };

    return (
        <>
            <div className={`flex flex-col gap-2 w-full pb-6 ${isCardDetails ? '' : 'px-5'}`}>
                <div className="flex flex-col gap-2 bg-pale-blue p-4 rounded-[8px]">
                    <h3 className="font-['Open_Sans'] font-semibold text-[20px] text-brand-dark leading-normal mb-2 text-center md:text-left">
                        Shop online at {deal.brand?.name} and earn{' '}
                        {deal.brand?.affiliateBonus || deal.brand?.affiliateRebate}% +
                    </h3>

                    <div className="flex flex-col md:flex-row gap-2 grow-1 stretch p-4 pr-2 bg-white align-middle items-center rounded-[8px]">
                        <p className="flex-grow font-semibold text-[16px] text-grey-darkest leading-[1.5]">
                            {deal.title}
                        </p>

                        <span className="flex flex-row gap-2 items-center">
                            <button
                                onClick={clickDetails}
                                className="text-brand text-[13px] font-semibold underline cursor-pointer px-2"
                            >
                                Details
                            </button>

                            {isCoupon ? (
                                <span className="flex flex-row gap-2 items-center relative">
                                    {showCopiedIndicator && (
                                        <span className="absolute -top-12 left-1/2 -translate-x-1/2 flex flex-col items-center">
                                            <span className="bg-dark-blue text-white text-[13px] font-semibold px-3 py-1 rounded whitespace-nowrap flex items-center gap-1">
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    className="text-[13px]"
                                                />
                                                Copied
                                            </span>
                                            <span className="w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-dark-blue" />
                                        </span>
                                    )}
                                    {couponRevealed ? (
                                        <button
                                            onClick={handleCouponClick}
                                            className="flex items-center gap-[6px] text-[13px] font-semibold text-dark-blue bg-white border-[1.5px] border-dashed border-dark-blue py-[8px] px-[16px] cursor-pointer"
                                        >
                                            <FontAwesomeIcon
                                                icon={faTicket}
                                                className="text-[14px] text-dark-blue"
                                            />
                                            {deal.couponCode}
                                        </button>
                                    ) : (
                                        <Btn
                                            onClick={handleCouponClick}
                                            isSecondary
                                            isSmall
                                            className="!text-[13px] !px-[20px] !py-[10px] font-semibold !whitespace-nowrap shrink-0"
                                        >
                                            <span className="flex items-center gap-[6px] whitespace-nowrap">
                                                Shop Coupon
                                                <ExternalLinkArrow
                                                    className="w-2.5 h-2.5"
                                                    color={colors.brand}
                                                />
                                            </span>
                                        </Btn>
                                    )}
                                </span>
                            ) : (
                                <Btn
                                    onClick={handleDealClick}
                                    isSecondary
                                    isSmall
                                    className="!text-[13px] !px-[20px] !py-[10px] font-semibold !whitespace-nowrap shrink-0"
                                >
                                    <span className="flex items-center gap-[6px] whitespace-nowrap">
                                        Shop Deal
                                        <ExternalLinkArrow
                                            className="w-2.5 h-2.5"
                                            color={colors.brand}
                                        />
                                    </span>
                                </Btn>
                            )}
                        </span>
                    </div>
                </div>

                <p className="font-normal text-[12px] text-grey-medium leading-[16px]">
                    When you buy through the link on {Branding}, we may earn a commission.
                </p>
            </div>
        </>
    );
}
